import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import Logger, { namedError } from '../../Utils/Logger';
import { parseFrontendVersion, isFrontendOlder } from './versionUtils';
import versionQueryDocument from './AvenyServiceVersionQuery.gql';
import { AvenyServiceVersionQuery } from './_QueryTypes/AvenyServiceVersionQuery';

/**
 * Custom React hook to check for version mismatch between frontend and backend.
 *
 * This hook queries the backend for its version and compares it to the frontend version.
 * If the frontend build is older than the backend, it logs the mismatch and can be used to block UI or instruct a reload.
 * The version check can be disabled via a global `window.env.disableVersionCheck` flag.
 *
 * @returns An object containing:
 * - `mismatch`: Indicates if there is a version mismatch (frontend is older than backend).
 * - `loading`: Indicates if the version check is in progress.
 * - `error`: Any error encountered during the version check.
 * - `refetch`: Function to manually refetch the version data.
 * - `backendVersion`: The backend version string (e.g., "1.2.3").
 * - `frontendVersion`: The frontend version string.
 */
export const useVersionMismatch = (): {
  mismatch: boolean;
  loading: boolean;
  error?: Error;
  refetch: () => void;
  backendVersion?: string;
  frontendVersion?: string;
} => {
  const disable = (window as any)?.env?.disableVersionCheck === true;

  const { data, loading, error, refetch } = useQuery<AvenyServiceVersionQuery>(
    versionQueryDocument,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      skip: disable,
    }
  );

  const hasLoggedRef = useRef(false);
  const frontendParsed = parseFrontendVersion(config.version);
  const backend = data?.AvenyServiceVersion;
  const mismatch = disable
    ? false
    : !!backend && frontendParsed
    ? isFrontendOlder(frontendParsed, backend)
    : false;

  useEffect(() => {
    if (
      !disable &&
      backend &&
      frontendParsed &&
      mismatch &&
      !hasLoggedRef.current
    ) {
      hasLoggedRef.current = true;
      Logger.log(
        'info',
        'FrontendBackendVersionMismatch',
        'Frontend bygg är äldre än backend. Blockerar UI och instruerar reload.',
        namedError('FrontendBackendVersionMismatch', 'Frontend build är äldre'),
        {
          frontendVersion: config.version,
          backendVersion: backend.versionString,
          backendNumeric: { ...backend },
        },
        {
          fingerprint: 'FrontendBackendVersionMismatch',
          reporter: 'version-check',
        }
      );
    }
  }, [backend, frontendParsed, mismatch, disable]);

  // When disabled, present as immediately non-mismatching and not loading.
  if (disable) {
    return {
      mismatch: false,
      loading: false,
      error: undefined,
      refetch,
      backendVersion: undefined,
      frontendVersion: undefined,
    };
  }
  return {
    mismatch,
    loading,
    error,
    refetch,
    backendVersion: `${backend?.major}.${backend?.minor}.${backend?.release}`,
    frontendVersion: config?.version,
  };
};
